import { ActionTakenType, LocationPickerLocation, MilesToMeters, UnitStandard, createActionTaken } from "@swivl-mono/types";
import { Slide } from "pure-react-carousel";
import React from "react";
import { createPortal } from "react-dom";
import { FaChevronDown, FaChevronUp, FaSpinner } from "react-icons/fa6";

import { Config } from "../../../../../Config/Config";
import { LanguageContext, t } from "../../../../../i18n/strings";
import { MessageHandlerInterface } from "../../../../../messages/MessageHandler";
import { ListItemData } from "../../../ListItem";
import { MetersToMiles } from "./MetersToMiles";

export const SelectedLocationCell: React.FC<{ location: any }> = (props) => {
  const language = React.useContext(LanguageContext);
  const { location } = props;
  const imageSRC = location?.AvatarURL ? location.AvatarURL : "https://swivl-cdn.s3.us-west-1.amazonaws.com/temp-store.png";
  const addressLink = encodeURIComponent(`${location?.Address} ${location?.City}, ${location?.State} ${location?.PostalCode}`);

  return (
    <div className="scc-SelectedLocation">
      <div className="scc-selectedLocationCellWrapper">
        <div className="scc-LocationCell">
          <div className="scc-locationImage">
            <img src={imageSRC} />
          </div>
          <div className="scc-locationText">
            <b>{location?.Name}</b>
            {location?.Address}
            <br />
            {location?.City}, {location?.State} {location?.PostalCode}
          </div>
        </div>

        <div className="scc-viewMap">
          <a href={"https://maps.google.com/?q=" + addressLink} target="_blank">
            {t("locationPickerViewMap", language)}
          </a>
        </div>
      </div>
    </div>
  );
};

interface LocationCellProps {
  location: LocationPickerLocation;
  messageHandler: MessageHandlerInterface;
  item: ListItemData;
}

export const LocationCell: React.FC<LocationCellProps> = (props) => {
  const [startX, setStartX] = React.useState(0);

  const imageSRC = props.location.imageURL ? props.location.imageURL : "https://swivl-cdn.s3.us-west-1.amazonaws.com/temp-store.png";

  const mouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setStartX(e.clientX);
  };

  const locationSelected = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!Config.canPressButtons) {
      return;
    }
    if (Math.abs(e.clientX - startX) > 5) {
      // This checks to make sure we weren't dragging.
      return;
    }

    const messageId = props.item.message.uuid;
    const actionTaken = createActionTaken(ActionTakenType.Selected, props.item.id, props.location.data);

    props.messageHandler.actionTaken(messageId, actionTaken);
    // this.setState({selectTwo:true});
  };

  return (
    <div className="scc-LocationCell swivl-primaryBackgroundHover swivl-primaryTextHover" onMouseDown={mouseDown} onMouseUp={locationSelected}>
      <div className="scc-locationImage">
        <img src={imageSRC} />
      </div>
      <div className="scc-locationText swivl-primaryBackgroundHover">
        <b>{props.location.name}</b>
        {props.location.address}
      </div>
    </div>
  );
};

interface RadiusMenuPortalProps {
  pos: { top: number; left: number; width: number };
  distanceUnit?: UnitStandard;
  searchRadiusInMeters: number;
  onSelect: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

const RADIUS_STEPS = [5, 10, 25, 50, 100] as const;
const RADIUS_MENU_CLASS = "scc-radiusOptionsMenu";

const RadiusMenuPortal = ({ pos, distanceUnit, searchRadiusInMeters, onSelect }: RadiusMenuPortalProps) => {
  const isMetric = distanceUnit === UnitStandard.METRIC;
  const menu = (
    <div className={RADIUS_MENU_CLASS} style={{ top: pos.top, left: pos.left, width: pos.width }}>
      {RADIUS_STEPS.map((step) => {
        const title = isMetric ? `Kilometers_${step}` : `Miles_${step}`;
        const value = isMetric ? step * 1000 : MilesToMeters[title as keyof typeof MilesToMeters];
        return (
          <a key={title} title={title} onClick={onSelect} className={searchRadiusInMeters === value ? "scc-selectedRadiusOption" : ""}>
            {step}
          </a>
        );
      })}
    </div>
  );
  return createPortal(menu, document.body) as React.ReactElement;
};

interface SearchCellProps {
  currentZip: string;
  searchNewZip: (newZip: string) => void;
  searchRadiusInMeters: number;
  messageHandler: MessageHandlerInterface;
  item: ListItemData;
  setShouldFocusOnControl(control?: string): void;
  shouldFocusOnControl?: string;
  distanceUnit?: UnitStandard;
}
export const SearchCell: React.FC<SearchCellProps> = (props) => {
  const language = React.useContext(LanguageContext);
  const [zip, setZip] = React.useState(props.currentZip);
  const [searchRadiusInMeters, setSearchRadiusInMeters] = React.useState(props.searchRadiusInMeters);
  const [showMenu, setShowMenu] = React.useState(false);
  const [menuPos, setMenuPos] = React.useState<{ top: number; left: number; width: number } | null>(null);
  const [isSearching, setIsSearching] = React.useState(false);
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>();
  const triggerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (props.shouldFocusOnControl === "radius") {
      setShowMenu(true);
      props.setShouldFocusOnControl(undefined);
    }
    if (props.shouldFocusOnControl === "zip" && inputRef.current) {
      inputRef.current.focus();
      props.setShouldFocusOnControl(undefined);
    }
  }, [props.shouldFocusOnControl]);

  React.useEffect(() => {
    setZip(props.currentZip);
  }, [props.currentZip]);
  React.useEffect(() => {
    setSearchRadiusInMeters(props.searchRadiusInMeters);
  }, [props.searchRadiusInMeters]);
  React.useEffect(() => {
    if (!hasLoaded) {
      setHasLoaded(true);
      return;
    }
    searchForLocation();
  }, [searchRadiusInMeters]);

  const onChange = (e) => {
    setZip(e.target.value);
  };
  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  React.useEffect(() => {
    if (!showMenu) return;
    let rafId: number;
    const update = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setMenuPos((prev) =>
          prev && prev.top === rect.top && prev.left === rect.left && prev.width === rect.width
            ? prev
            : { top: rect.top, left: rect.left, width: rect.width }
        );
      }
      rafId = requestAnimationFrame(update);
    };
    rafId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(rafId);
  }, [showMenu]);

  React.useEffect(() => {
    if (!showMenu) return;
    const handleDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if ((target as HTMLElement)?.closest?.(`.${RADIUS_MENU_CLASS}`)) return;
      setShowMenu(false);
    };
    const t = window.setTimeout(() => document.addEventListener("mousedown", handleDocClick), 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("mousedown", handleDocClick);
    };
  }, [showMenu]);

  const searchForLocation = () => {
    if (!Config.canPressButtons) {
      return;
    }

    setIsSearching(true);
    props.messageHandler
      .getNewLocations(props.item.message.id ? props.item.message.id : 0, props.item.message.uuid, zip, searchRadiusInMeters)
      .then((_) => {
        setIsSearching(false);
      })
      .catch((e) => {
        setIsSearching(false);
      });
  };

  const radiusOptionClicked = (e) => {
    const title: string = e.currentTarget.title;
    if (title.startsWith("Kilometers")) {
      setSearchRadiusInMeters(parseInt(title.split("_")[1]) * 1000);
    } else {
      setSearchRadiusInMeters(MilesToMeters[title]);
    }
    setShowMenu(false);
  };

  const searchClicked = () => {
    searchForLocation();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      searchForLocation();
    }
  };

  return (
    <div className="scc-LocationCell scc-SearchLocationCell">
      <span className={`scc-startText ${language}`}>{t("locationPickerSearch", language)}</span>
      {props.distanceUnit === UnitStandard.METRIC ? (
        <>
          <div className="scc-radiusOptions">
            <div className="scc-selectedRadius" onClick={toggleMenu} ref={triggerRef}>
              <div className="scc-selectedRadiusValue">{searchRadiusInMeters / 1000}</div>
              <div className="scc-selectedRadiusTab swivl-primaryBackgroundHover">{showMenu ? <FaChevronUp /> : <FaChevronDown />}</div>
            </div>
          </div>
          <span className={`scc-middleText ${language}`}>{t("locationPickerKmOfPostalCode", language)}</span>
        </>
      ) : (
        <>
          <div className="scc-radiusOptions">
            <div className="scc-selectedRadius" onClick={toggleMenu} ref={triggerRef}>
              <div className="scc-selectedRadiusValue">{MetersToMiles(searchRadiusInMeters)}</div>
              <div className="scc-selectedRadiusTab swivl-primaryBackgroundHover">{showMenu ? <FaChevronUp /> : <FaChevronDown />}</div>
            </div>
          </div>
          <span className={`scc-middleText ${language}`}>{t("locationPickerMilesOfPostalCode", language)}</span>
        </>
      )}

      <div className="scc-locationSearchInputAndButton">
        <input value={zip} onChange={onChange} onKeyDown={handleKeyDown} ref={inputRef as any} />
        <button className={`scc-locationSearchButton swivl-primaryButton ${language}`} onClick={searchClicked}>
          {isSearching ? <FaSpinner className="spin" /> : t("locationPickerSearchButton", language)}
        </button>
      </div>

      {showMenu && menuPos ? (
        <RadiusMenuPortal
          pos={menuPos}
          distanceUnit={props.distanceUnit}
          searchRadiusInMeters={searchRadiusInMeters}
          onSelect={radiusOptionClicked}
        />
      ) : null}
    </div>
  );
};

interface LocationCardProps {
  index: number;
  showSearch?: boolean;
  locations: LocationPickerLocation[];
  currentZip: string;
  searchRadiusInMeters?: number;
  distanceUnit?: UnitStandard;
  searchNewZip?: (newZip: string) => void;
  messageHandler?: MessageHandlerInterface;
  item?: ListItemData;
  isNoResults?: boolean;
  setShouldFocusOnControl(control: string): void;
  shouldFocusOnControl?: string;
}

export const LocationCard: React.FC<LocationCardProps> = (props) => {
  const language = React.useContext(LanguageContext);
  const {
    index,
    locations,
    showSearch,
    currentZip,
    searchNewZip,
    searchRadiusInMeters,
    messageHandler,
    isNoResults,
    setShouldFocusOnControl,
    shouldFocusOnControl
  } = props;
  const searchCell = showSearch ? (
    <SearchCell
      currentZip={currentZip}
      searchNewZip={searchNewZip as any}
      searchRadiusInMeters={searchRadiusInMeters as any}
      messageHandler={messageHandler as any}
      item={props.item!}
      setShouldFocusOnControl={setShouldFocusOnControl}
      shouldFocusOnControl={shouldFocusOnControl}
      distanceUnit={props.distanceUnit}
    />
  ) : null;
  const cells = isNoResults ? (
    <div className="scc-noResults">{t("locationPickerNoResults", language)}</div>
  ) : (
    locations.map((item) => <LocationCell key={item.id} location={item} messageHandler={messageHandler as any} item={props.item as any} />)
  );

  return (
    <Slide index={index} key={"slide_" + index} innerClassName="scc-locationSlide">
      {cells}
      {searchCell}
    </Slide>
  );
};
